import { ReactNode, JSXElementConstructor } from 'react';
import { checkIsAdminManagement } from 'method';
import { CONTENT } from 'constants/content';
import config, { Project } from 'app.config';

export * from './exportColumn';

export const FILE_BATCH_SIZE = 50;
export const DEFAULT_DAYS_TO_SHIP = 2;
export const DEFAULT_DAYS_TO_RETURN = 3;

export const LIMITS = Object.freeze({
  SUPPLIER_ID: 10,
  FIELDS: config.project === Project.SupplyChainManagement ? 9 : 16,
  RANGE: 90,
  ORDER_RANGE: 30,
  RETURN_RANGE: 61,
});

const isAdminManagement = checkIsAdminManagement();
export interface XlsxColumn {
  key: string | string[];
  label: string;
  transform?: Function;
  columnKey?: string;
}

export interface OrderInfoEntry {
  key: string;
  label: string | Function;
  icon?: ReactNode;
  defaultValue?: string;
  expansion?: Function;
  expansionInfo?: ReactNode;
  transform?: Function;
  render?: Function;
}

export interface ScmSkuInfo {
  sku_id: string;
  sku_name: string;
  price: string;
  quantity: number;
}

export interface OrderSku extends ScmSkuInfo {
  item_sku: string;
  item_name: string;
  variation_sku: string;
  variation_name: string;
}

export interface ReturnSku extends ScmSkuInfo {
  platform_return_id: string;
}

export interface LogisticsLog {
  created_at: string;
  logistics_name: string;
  logistics_number: string;
  logs: {
    status: string;
    status_code: string;
    status_time: string;
  }[];
}

export interface ReturnLines {
  id: number;
  price: number;
  quantity: number;
  sku_id: string;
  sku_name: string;
}

export interface ShopeeOrder {
  ordersn: string;
  ship_by_date?: string;
  shipping_carrier: string;
}

export interface OrderStatusLog {
  status: number;
  created_at: string;
}

export interface HiddenPersonalInfo {
  $key: string;
  buyer_address?: string;
  buyer_name?: string;
  buyer_zipcode?: string;
  buyer_phone?: string;
  pickup_address?: string;
  pickup_name?: string;
  pickup_zipcode?: string;
  pickup_phone?: string;
}

export interface ScmInfo {
  id: number;
  create_time: string;
  platform_order_id: string;
  shop_name?: string;
  supplier_id: number;
  supplier_name: string;
  status: number;
  platform_status: number;
  logistics: LogisticsLog[];
  currency: string;
  tracking_no: string;
  issue_status: number;
  _hidden: HiddenPersonalInfo;
}
export interface ScmReturn extends ScmInfo {
  platform_return_id: string;
  shopee_return_id: number;
  reason: number;
  text_reason: string;
  refund_amount: number;
  update_time: string;
  due_date: string;
  dispute_reason: number;
  dispute_text_reason: string;
  needs_logistics: boolean;
  amount_before_discount: number;
  user_name: string;
  user_email: string;
  images: string[];
  lines: ReturnSku[];
  order: ScmOrder;
  logs: OrderStatusLog[];
  shop_id: string;
  pickup_name: string;
  pickup_phone: string;
  pickup_address: string;
}

export interface ScmOrder extends ScmInfo {
  [x: string]: any;
  pay_time: string;
  payment_method: string;
  buyer_id: string;
  buyer_name: string | null;
  buyer_phone: string;
  buyer_address: string | null;
  buyer_zipcode: string | null;
  message_to_seller: string;
  days_to_ship: number;
  note: string;
  sku_list: OrderSku[];
  price: number;
  amount: number;
  update_time: string;
  buyer_cancel_reason: string;
  country: string;
  order_type: number;
  shipping_carrier: string;
  shipping_fee: number;
  created_at: string;
  updated_at: string;
  logistics_name: string;
  ship_by_date: string;
}

export interface SupportCase extends ScmInfo {
  id: number;
  resource_id: string;
  sheet_id: string;
  created_at: number;
  update_at: number;
  type: SUPPORT_CASE_TYPE;
  status: number;
  content: string;
  supplier_id: number;
  supplier_name: string;
  priority: number;
}

export enum Status_Icon_Type {
  NORMAL = 0,
  SUPPORT_CASE = 1,
}

export enum PLATFORM_STATUS {
  ALL = -1,
  PENDING = 0,
  UNPAID = 10,
  SHIPPING = 20,
  COMPLETED = 30,
  CANCELLED = 40,
  IN_CANCEL = 45,
  RETURNING_OLD = 50,
  TO_RETURN = 70,
  RETURNING = 71,
  ARGUING = 72,
  TO_REFUND = 73,
  ARGUED = 74,
  RETURN_CANCELLED = 75,
  RETURN_COMPLETED = 76,
  REVIEW = 77,
  RETURN = 200,
  // unused status
  // RETURN_ITEM_FAKE = 78,
}

export const PLATFORM_STATUS_MAP = {
  [PLATFORM_STATUS.ALL]: '全部',
  [PLATFORM_STATUS.PENDING]: '待出貨',
  [PLATFORM_STATUS.UNPAID]: '未付款',
  [PLATFORM_STATUS.SHIPPING]: '運送中',
  [PLATFORM_STATUS.COMPLETED]: '已完成',
  [PLATFORM_STATUS.CANCELLED]: '不成立',
  [PLATFORM_STATUS.IN_CANCEL]: '待取消',
  [PLATFORM_STATUS.RETURNING_OLD]: '退貨中',
  [PLATFORM_STATUS.TO_RETURN]: '待退貨',
  [PLATFORM_STATUS.RETURNING]: '待驗收',
  [PLATFORM_STATUS.ARGUING]: '爭議協調',
  [PLATFORM_STATUS.TO_REFUND]: '待退款',
  [PLATFORM_STATUS.ARGUED]: '爭議確定',
  [PLATFORM_STATUS.RETURN_CANCELLED]: '不成立',
  [PLATFORM_STATUS.RETURN_COMPLETED]: '已完成',
  [PLATFORM_STATUS.REVIEW]: '蝦皮審核',
  // unused status
  // [PLATFORM_STATUS.RETURN_ITEM_FAKE]: '假貨爭議',
};

export const PLATFORM_STATUS_COLOR_MAP = {
  [PLATFORM_STATUS.ALL]: '全部',
  [PLATFORM_STATUS.PENDING]: 'volcano',
  [PLATFORM_STATUS.UNPAID]: '',
  [PLATFORM_STATUS.SHIPPING]: 'gold',
  [PLATFORM_STATUS.COMPLETED]: 'green',
  [PLATFORM_STATUS.CANCELLED]: 'red',
  [PLATFORM_STATUS.IN_CANCEL]: 'blue',
  [PLATFORM_STATUS.RETURNING_OLD]: 'purple',
  [PLATFORM_STATUS.TO_RETURN]: 'volcano',
  [PLATFORM_STATUS.RETURNING]: 'purple',
  [PLATFORM_STATUS.ARGUING]: 'blue',
  [PLATFORM_STATUS.TO_REFUND]: 'cyan',
  [PLATFORM_STATUS.ARGUED]: 'gold',
  [PLATFORM_STATUS.RETURN_CANCELLED]: 'red',
  [PLATFORM_STATUS.RETURN_COMPLETED]: 'green',
};

export const UNKNOWN_PLATFORM_STATUS = '未知狀態';

export const PLATFORM_STATUS_LOG_MAP = {
  [PLATFORM_STATUS.ALL]: '全部訂單',
  [PLATFORM_STATUS.PENDING]: '新訂單',
  [PLATFORM_STATUS.UNPAID]: '訂單未付款',
  [PLATFORM_STATUS.SHIPPING]: '訂單已出貨',
  [PLATFORM_STATUS.COMPLETED]: '訂單已完成',
  [PLATFORM_STATUS.CANCELLED]: '訂單已取消',
  [PLATFORM_STATUS.IN_CANCEL]: '訂單待取消',
  [PLATFORM_STATUS.RETURNING_OLD]: '訂單退貨中',
  [PLATFORM_STATUS.TO_RETURN]: '待退貨',
  [PLATFORM_STATUS.RETURNING]: '待驗收',
  [PLATFORM_STATUS.ARGUING]: '爭議協調',
  [PLATFORM_STATUS.TO_REFUND]: '待退款',
  [PLATFORM_STATUS.ARGUED]: '爭議確定',
  [PLATFORM_STATUS.RETURN_CANCELLED]: '退貨取消',
  [PLATFORM_STATUS.RETURN_COMPLETED]: '退貨完成',
  [PLATFORM_STATUS.REVIEW]: '蝦皮審核',
  // unused status
  // [PLATFORM_STATUS.RETURN_ITEM_FAKE]: '假貨爭議',
};

export enum ORDER_STATUS {
  UNPAID = 0,
  READY_TO_SHIP = 1,
  // unused status
  // RETRY_SHIP = 2,
  SHIPPED = 3,
  TO_CONFIRM_RECEIVE = 4,
  IN_CANCEL = 5,
  CANCELLED = 6,
  TO_RETURN = 7,
  COMPLETED = 8,
  PROCESSED = 9,
}

export const ORDER_STATUS_NAME = {
  [ORDER_STATUS.UNPAID]: '未付款',
  [ORDER_STATUS.READY_TO_SHIP]: '可出貨',
  // unused status
  // [ORDER_STATUS.RETRY_SHIP]: 'RETRY_SHIP',
  [ORDER_STATUS.SHIPPED]: '配送中',
  [ORDER_STATUS.TO_CONFIRM_RECEIVE]: '確認收貨',
  [ORDER_STATUS.IN_CANCEL]: '取消中(待取消)',
  [ORDER_STATUS.CANCELLED]: '不成立',
  [ORDER_STATUS.TO_RETURN]: '退貨',
  [ORDER_STATUS.COMPLETED]: '訂單已完成',
  [ORDER_STATUS.PROCESSED]: '處理中',
};

export const ORDER_SYNC_KEY = 'order-sync';

export enum RETURN_STATUS {
  REQUESTED = 0,
  ACCEPTED = 1,
  CANCELLED = 2,
  JUDGING = 3,
  REFUND_PAID = 4,
  CLOSED = 5,
  PROCESSING = 6,
  SELLER_DISPUTE = 7,
}

export const RETURN_STATUS_NAME = {
  [RETURN_STATUS.REQUESTED]: '已請求',
  [RETURN_STATUS.ACCEPTED]: '已接受',
  [RETURN_STATUS.CANCELLED]: '不成立',
  [RETURN_STATUS.JUDGING]: '判定中',
  [RETURN_STATUS.REFUND_PAID]: '已退款',
  [RETURN_STATUS.CLOSED]: '已關閉',
  [RETURN_STATUS.PROCESSING]: '處理中',
  [RETURN_STATUS.SELLER_DISPUTE]: '賣方爭議',
};

export enum RETURN_REASON {
  NONE = 0,
  NOT_RECEIPT = 1,
  WRONG_ITEM = 2,
  ITEM_DAMAGED = 3,
  DIFFERENT_DESCRIPTION = 4,
  MUTUAL_AGREE = 5,
  OTHER = 6,
  ITEM_WRONG_DAMAGED = 7,
  CHANGE_MIND = 8,
  ITEM_MISSING = 9,
  EXPECTATION_FAILED = 10,
  ITEM_FAKE = 11,
  PHYSICAL_DMG = 12,
  FUNCTIONAL_DMG = 13,
  SUSPICIOUS_PARCEL = 14,
}

export const RETURN_REASON_NAME = {
  [RETURN_REASON.NONE]: '商品外表毀損/部分功能不能使用',
  [RETURN_REASON.NOT_RECEIPT]: '我還沒收到商品',
  [RETURN_REASON.WRONG_ITEM]: '我收到不對的商品',
  [RETURN_REASON.ITEM_DAMAGED]: '商品外表毀損、功能異常',
  [RETURN_REASON.DIFFERENT_DESCRIPTION]: '與商品描述不符',
  [RETURN_REASON.MUTUAL_AGREE]: '雙方同意',
  [RETURN_REASON.OTHER]: '其他',
  [RETURN_REASON.ITEM_WRONG_DAMAGED]: '項目錯誤損壞',
  [RETURN_REASON.CHANGE_MIND]: '改變心意',
  [RETURN_REASON.ITEM_MISSING]: '商品缺件',
  [RETURN_REASON.EXPECTATION_FAILED]: '商品不如預期',
  [RETURN_REASON.ITEM_FAKE]: '仿冒商品',
  [RETURN_REASON.PHYSICAL_DMG]: '商品外表毀損',
  [RETURN_REASON.FUNCTIONAL_DMG]: '功能毀損',
  [RETURN_REASON.SUSPICIOUS_PARCEL]: '收到空包裹/詐騙包裹',
};

export const LOGISTICS_OPTIONS = {
  HCT: 0,
  KTJ: 1,
  PELICAN: 2,
  TCAT: 3,
  SULI: 4,
  POST: 5,
  TY: 6,
  C2C: 7,
  OTHER: 8,
  SPEEDY: 101,
};

export const LOGISTICS_OPTIONS_MAP: {
  [key: number]: string;
} = {
  [LOGISTICS_OPTIONS.HCT]: '新竹物流',
  [LOGISTICS_OPTIONS.KTJ]: '嘉里大榮',
  [LOGISTICS_OPTIONS.PELICAN]: '宅配通',
  [LOGISTICS_OPTIONS.TCAT]: '黑貓',
  [LOGISTICS_OPTIONS.SULI]: '速利',
  [LOGISTICS_OPTIONS.POST]: '郵局',
  [LOGISTICS_OPTIONS.TY]: '通盈',
  [LOGISTICS_OPTIONS.C2C]: '賣家自送',
  [LOGISTICS_OPTIONS.OTHER]: '其他',
};

export enum INTEGRATED {
  TRUE = 'True',
  FALSE = 'False',
}

export const INTEGRATED_NAME = {
  [INTEGRATED.TRUE]: '整合物流',
  [INTEGRATED.FALSE]: '非整合物流',
};

export enum SHIPPING_CARRIER_OPTIONS {
  SEVEN_ELEVEN = '7-11',
  SPX = '蝦皮店到店',
}

export const SHIPPING_CARRIER = {
  [SHIPPING_CARRIER_OPTIONS.SEVEN_ELEVEN]: '7-11',
  [SHIPPING_CARRIER_OPTIONS.SPX]: '蝦皮店到店',
};

export enum LOGISTICS_TYPE {
  HOME_DELIVERY = '1',
  SHIPPING_CARRIER = '2',
}

export const LOGISTICS_TYPE_OPTIONS = {
  [LOGISTICS_TYPE.HOME_DELIVERY]: '宅配批次出貨',
  [LOGISTICS_TYPE.SHIPPING_CARRIER]: '超取批次出貨',
};

export type TabStatusConfigProps<T> = {
  status: T | T[];
  label?: ReactNode;
  icon?: ReactNode;
};

export type TabStatus<T = any> = T | TabStatusConfigProps<T>;

export class TabStatusItem<T = any> {
  protected _statusList: T[];
  protected _label?: ReactNode;
  protected _icon?: ReactNode;
  constructor(tabStatus: TabStatus<T>) {
    if (typeof tabStatus === 'number') {
      this._statusList = [tabStatus];
      this._label = null;
      this._icon = '';
    } else if (typeof tabStatus === 'string') {
      this._statusList = [tabStatus];
      this._label = tabStatus;
      this._icon = '';
    } else {
      const { status, label, icon } = tabStatus as TabStatusConfigProps<T>;
      this._statusList = Array.isArray(status) ? status : [status];
      this._label = label;
      this._icon = icon;
    }
  }
  get statusList(): T[] {
    return this._statusList;
  }
  get status(): T {
    return this._statusList[0];
  }
  get key(): string {
    return this._statusList.join(',');
  }
}

export type TabConfig = {
  key: string;
  name: string;
  component: JSXElementConstructor<any>;
};

export enum SEARCH_OPTION {
  SYSTEM_ORDER = 'order_id',
  SYSTEM_RETURN = 'return_id',
  SYSTEM_TRACKING = 'tracking_no',
}

export const ORDER_SEARCH = {
  [SEARCH_OPTION.SYSTEM_ORDER]: CONTENT.PLATFORM_ORDER_ID,
  [SEARCH_OPTION.SYSTEM_TRACKING]: CONTENT.TRACKING_NO,
};

export const RETURN_SEARCH = {
  [SEARCH_OPTION.SYSTEM_ORDER]: CONTENT.PLATFORM_ORDER_ID,
  [SEARCH_OPTION.SYSTEM_RETURN]: CONTENT.PLATFORM_RETURN_ID,
  [SEARCH_OPTION.SYSTEM_TRACKING]: CONTENT.TRACKING_NO,
};

export enum ISSUE_STATUS {
  SUPPLIER = '1',
  ADMIN = '2',
}

export const ISSUE_STATUS_MAP = {
  [ISSUE_STATUS.SUPPLIER]: '蝦皮通知',
  [ISSUE_STATUS.ADMIN]: '賣家通知',
};

export enum SUPPORT_CASE_STATUS {
  ALL = -1,
  FINISH = 0,
  PENDING_TRACKING = 1,
  PENDING_REPLAY = 2,
}

export const SUPPORT_CASE_STATUS_NAME = {
  [SUPPORT_CASE_STATUS.ALL]: '全部',
  [SUPPORT_CASE_STATUS.FINISH]: '已結案',
  [SUPPORT_CASE_STATUS.PENDING_TRACKING]: isAdminManagement ? '待追蹤' : '待回覆',
  [SUPPORT_CASE_STATUS.PENDING_REPLAY]: isAdminManagement ? '待回覆' : '待追蹤',
};

export enum SUPPORT_CASE_SEARCH_OPTION {
  ORDER_ID = 'platform_order_id',
  RETURN_ID = 'platform_return_id',
  SKU_ID = 'sku_id',
  CASE_ID = 'id',
  SHEET_ID = 'sheet_id',
  SUPPLIER_ID = 'supplier_id',
}

export const SUPPORT_CASE_SEARCH = {
  [SUPPORT_CASE_SEARCH_OPTION.ORDER_ID]: CONTENT.PLATFORM_ORDER_ID,
  [SUPPORT_CASE_SEARCH_OPTION.RETURN_ID]: CONTENT.PLATFORM_RETURN_ID,
  [SUPPORT_CASE_SEARCH_OPTION.SKU_ID]: 'SKU ID',
  [SUPPORT_CASE_SEARCH_OPTION.CASE_ID]: 'Case ID',
  [SUPPORT_CASE_SEARCH_OPTION.SHEET_ID]: 'Sheet ID',
  [SUPPORT_CASE_SEARCH_OPTION.SUPPLIER_ID]: CONTENT.SUPPLIER_ID,
};

export enum SUPPORT_CASE_TYPE {
  ORDER = 'order',
  RETURN = 'return',
  SKU = 'sku',
}

export const SUPPORT_CASE_TYPE_NAME = {
  [SUPPORT_CASE_TYPE.ORDER]: '訂單詢問',
  [SUPPORT_CASE_TYPE.RETURN]: '退貨詢問',
  [SUPPORT_CASE_TYPE.SKU]: '商品詢問',
};

export enum SUPPORT_CASE_PRIORITY {
  HIGH = 0,
  MEDIUM = 1,
  LOW = 2,
}

export const SUPPORT_CASE_PRIORITY_NAME = {
  [SUPPORT_CASE_PRIORITY.HIGH]: 'High',
  [SUPPORT_CASE_PRIORITY.MEDIUM]: 'Medium',
  [SUPPORT_CASE_PRIORITY.LOW]: 'Low',
};

export const PLATFORM_STATUS_OPTIONS = Object.freeze([
  PLATFORM_STATUS.ALL,
  PLATFORM_STATUS.PENDING,
  PLATFORM_STATUS.SHIPPING,
  PLATFORM_STATUS.IN_CANCEL,
  PLATFORM_STATUS.COMPLETED,
  PLATFORM_STATUS.CANCELLED,
]);

export const PLATFORM_RETURN_STATUS_OPTIONS = Object.freeze([
  PLATFORM_STATUS.ALL,
  PLATFORM_STATUS.TO_RETURN,
  PLATFORM_STATUS.RETURNING,
  PLATFORM_STATUS.ARGUING,
  PLATFORM_STATUS.TO_REFUND,
  PLATFORM_STATUS.ARGUED,
  PLATFORM_STATUS.RETURN_COMPLETED,
  PLATFORM_STATUS.RETURN_CANCELLED,
]);
